
import {
  Vue, Component, Prop,
} from 'vue-property-decorator';
import { RuleCustomValue } from '@/types';
import {
  getStatusOptions,
  getTaskOptions,
  getActivityStatusOptions,
  getTaskStatusOptions,
  getEgridConnectionStatusOptions,
  getCommissioningStatusOptions,
  getAttachmentTypeOptions,
  getActivityStatusAttachmentTypeOptions,
  getAttachmentTypeLabel,
  getAssignmentTypeOptions,
} from '@/helpers/ruleHelpers';

@Component
export default class RuleCustomValueEditor extends Vue {
  @Prop({ type: Object, required: true }) readonly customValue!: RuleCustomValue;
  @Prop({ type: Array, default: () => [] }) readonly users!: any[];
  @Prop({ type: Array, default: () => [] }) readonly groups!: any[];
  @Prop({ type: Array, default: () => [] }) readonly categories!: any[];
  @Prop({ type: Array, default: () => [] }) readonly tags!: any[];
  @Prop({ type: Array, default: () => [] }) readonly templates!: any[];
  @Prop({ type: Array, default: () => [] }) readonly checklists!: any[];

  // Testo digitato negli autocomplete multipli: Vuetify lo lascia dopo la scelta
  // finché il menu è aperto, quindi lo azzeriamo noi (OwlCaveLabs/syndesia-l2c-support#33)
  search: string | null = null;

  mounted() {
    if (this.customValue.type === 'assignmentType' && this.$store.getters.getAssignmentTypes.length === 0) {
      this.$store.dispatch('fetchAssignmentTypes');
    }
  }

  // --- Entity types (user, group, category, tag, template, checklist) ---
  // Pattern del progetto: :value con ID primitivo, @change con lookup oggetto

  get entityId(): any {
    const val = this.customValue.value;
    if (!val) return this.customValue.multiple ? [] : null;
    if (this.customValue.multiple && Array.isArray(val)) {
      return val.map((v: any) => (typeof v === 'object' && v !== null ? v.id : v));
    }
    return typeof val === 'object' && val !== null ? val.id : val;
  }

  onEntityChange(idOrIds: any, items: any[]): void {
    let value: any;
    if (this.customValue.multiple && Array.isArray(idOrIds)) {
      value = idOrIds
        .map((id: any) => items.find((i: any) => i.id === id))
        .filter(Boolean);
      this.search = null;
    } else {
      value = idOrIds != null
        ? items.find((i: any) => i.id === idOrIds) || null
        : null;
    }
    this.$emit('input', { ...this.customValue, value });
  }

  // --- Tipi primitivi (text, status, task, etc.) ---

  get localValue(): any {
    return this.customValue.value;
  }

  set localValue(val: any) {
    this.$emit('input', { ...this.customValue, value: val });
  }

  get localBooleanValue(): boolean {
    return !!this.customValue.booleanValue;
  }

  set localBooleanValue(val: boolean) {
    this.$emit('input', { ...this.customValue, booleanValue: val });
  }

  // --- Opzioni per v-select ---

  get statusOptions() {
    return getStatusOptions();
  }

  get taskOptions() {
    return getTaskOptions();
  }

  get activityStatusOptions() {
    return getActivityStatusOptions();
  }

  get taskStatusOptions() {
    return getTaskStatusOptions();
  }

  get egridConnectionStatusOptions() {
    return getEgridConnectionStatusOptions();
  }

  get commissioningStatusOptions() {
    return getCommissioningStatusOptions();
  }

  get attachmentTypeOptions() {
    const base = this.customValue.filter === 'activityStatus'
      ? getActivityStatusAttachmentTypeOptions()
      : getAttachmentTypeOptions();
    // Fallback: se il value corrente non è nella lista (es. regola legacy con
    // tipo deprecato come dicoElettrica/visura/autotest, oppure tipo escluso
    // dal filtro activityStatus), aggiungilo dinamicamente così la <v-select>
    // mostra il valore e l'utente non lo azzera salvando senza toccarlo.
    const current = this.customValue.value;
    if (typeof current === 'string' && current && !base.some((o) => o.value === current)) {
      return [...base, { value: current, text: getAttachmentTypeLabel(current) }];
    }
    return base;
  }

  get assignmentTypeOptions() {
    return getAssignmentTypeOptions(this.$store.getters.getAssignmentTypes);
  }
}
